import { useEffect, useState } from 'preact/hooks';
import PropTypes from 'prop-types';

import WarningIcon from '../icons/WarningIcon';
import styleSheet from './YouTubeVideo.module.scss';
import { buildEmbedUrl, buildThumbnailUrl } from '../../../helpers/youtube';

const styles = styleSheet.locals || {};

const PLACEHOLDER_THUMB_WIDTH = 120;

const YOUTUBE_HOSTS = [
    'img.youtube.com',
    'www.youtube.com',
    'www.youtube-nocookie.com'
];

const isYouTubeCspViolation = (event) =>
    event.disposition !== 'report' &&
    YOUTUBE_HOSTS.some((host) => event.blockedURI?.includes(host));

/**
 * A YouTube video rendered as a click-to-load facade: show the thumbnail, load
 * the player only after the fan clicks (keeps the page light, no YouTube cookies
 * until play).
 *
 * Activation can be controlled by the parent (pass `activated` + `onActivate`)
 * so only one video loads at a time; otherwise it manages its own.
 *
 * @param {object} props - component props
 * @param {string} props.videoId - 11-char YouTube video ID
 * @param {string} [props.title] - accessible label
 * @param {boolean} [props.activated] - controlled activation state
 * @param {Function} [props.onActivate] - called when the fan clicks play
 * @param {string} [props.thumbnailQuality] - preferred thumbnail quality
 * @returns {object|null} the video element, or null when no videoId
 */
function YouTubeVideo({
    videoId,
    title,
    activated: activatedProp,
    onActivate,
    thumbnailQuality = 'maxresdefault'
}) {
    const [activatedState, setActivatedState] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const [cspBlocked, setCspBlocked] = useState(false);

    // violation events on the host document are the only way to tell a
    // CSP-blocked video apart from one that doesn't exist
    useEffect(() => {
        const onViolation = (event) => {
            if (isYouTubeCspViolation(event)) {
                setCspBlocked(true);
            }
        };
        document.addEventListener('securitypolicyviolation', onViolation);
        return () =>
            document.removeEventListener(
                'securitypolicyviolation',
                onViolation
            );
    }, []);

    const isControlled = typeof activatedProp === 'boolean';
    const activated = isControlled ? activatedProp : activatedState;

    const activate = () => {
        if (isControlled) {
            onActivate?.();
            return;
        }
        setActivatedState(true);
    };

    if (!videoId) {
        return null;
    }

    const label = title || 'Video';

    // csp block outranks unavailable: a blocked thumbnail also errors, but
    // the video itself may be perfectly fine
    if (cspBlocked || unavailable) {
        return (
            <div className={styles.youTubeVideo} data-cy="youtube-video">
                <style>{styleSheet.toString()}</style>
                <div
                    className={styles.unavailable}
                    data-cy={
                        cspBlocked
                            ? 'youtube-video-csp-blocked'
                            : 'youtube-video-unavailable'
                    }
                >
                    <WarningIcon aria-hidden="true" />
                    <p className={styles.unavailableText}>
                        {cspBlocked
                            ? 'This video is blocked by the Content ' +
                              'Security Policy. Contact the site ' +
                              'administrator to investigate.'
                            : 'We cannot render this video. Check the poll ' +
                              'configuration for errors.'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.youTubeVideo} data-cy="youtube-video">
            <style>{styleSheet.toString()}</style>

            {activated ? (
                <iframe
                    className={styles.frame}
                    src={buildEmbedUrl(videoId, { autoplay: true })}
                    title={label}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            ) : (
                <button
                    type="button"
                    className={styles.facade}
                    aria-label={`Play video: ${label}`}
                    onClick={activate}
                >
                    <img
                        className={styles.thumbnail}
                        src={buildThumbnailUrl(videoId, thumbnailQuality)}
                        alt=""
                        loading="lazy"
                        onLoad={(event) => {
                            const image = event.currentTarget;
                            if (image.naturalWidth > PLACEHOLDER_THUMB_WIDTH) {
                                return;
                            }
                            const fallback = buildThumbnailUrl(
                                videoId,
                                'hqdefault'
                            );
                            if (image.src !== fallback) {
                                image.src = fallback;
                            } else {
                                setUnavailable(true);
                            }
                        }}
                        onError={(event) => {
                            const fallback = buildThumbnailUrl(
                                videoId,
                                'hqdefault'
                            );
                            if (event.currentTarget.src !== fallback) {
                                event.currentTarget.src = fallback;
                            } else {
                                setUnavailable(true);
                            }
                        }}
                    />
                    <span className={styles.playButton} aria-hidden="true">
                        ▶
                    </span>
                </button>
            )}
        </div>
    );
}

YouTubeVideo.propTypes = {
    videoId: PropTypes.string,
    title: PropTypes.string,
    thumbnailQuality: PropTypes.string,
    activated: PropTypes.bool,
    onActivate: PropTypes.func
};

export default YouTubeVideo;
